import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFaceSmile,
  faBolt,
  faMoon,
  faDroplet,
  faCalendarDay,
  faPlus,
  faPen,
  faTrash,
  faCheck,
  faExclamationTriangle,
  faBell,
  faNotesMedical,
  faSpinner,
  faCapsules,
  faArrowRight,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useNavigate } from 'react-router-dom';
import womensHealthService, {
  DailyLog as DailyLogType,
  Symptom,
} from '../../../services/womensHealthService';
import BackButton from '../../../components/BackButton/BackButton';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import './DailyLog.css';
import { useProfile } from '../../../context/ProfileContext';
import { useServiceAccess } from '../../../context/ServiceAccessContext';

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
}

interface ChoiceOption {
  value: string;
  display_name: string;
}

interface ChoicesData {
  mood?: ChoiceOption[];
  energy_level?: ChoiceOption[];
  sleep_quality?: ChoiceOption[];
  acne_level?: ChoiceOption[];
}

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const formatDate = (iso: string): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateLong = (iso: string): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const todayISO = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const MOOD_EMOJIS: Record<string, string> = {
  happy: '😊',
  calm: '😌',
  anxious: '😰',
  sad: '😢',
  irritable: '😤',
  energetic: '⚡',
  tired: '😴',
  moody: '🌀',
};

const MOOD_COLORS: Record<string, string> = {
  happy: '#10b981',
  calm: '#06b6d4',
  anxious: '#f59e0b',
  sad: '#6366f1',
  irritable: '#ef4444',
  energetic: '#ec4899',
  tired: '#8b5cf6',
  moody: '#f97316',
};

const ENERGY_COLORS: Record<string, string> = {
  low: '#ef4444',
  medium: '#f59e0b',
  high: '#10b981',
};

const SLEEP_COLORS: Record<string, string> = {
  low: '#ef4444',
  medium: '#f59e0b',
  high: '#10b981',
};

/* ================================================================== */
/*  Toast Component                                                    */
/* ================================================================== */

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const iconMap: Record<string, IconDefinition> = {
    success: faCheck,
    error: faExclamationTriangle,
    info: faBell,
  };

  return (
    <div className={`wh-log-toast wh-log-toast-${type}`}>
      <span className="wh-log-toast-icon">
        <FontAwesomeIcon icon={iconMap[type]} />
      </span>
      <span className="wh-log-toast-message">{message}</span>
      <button className="wh-log-toast-close" onClick={onClose} aria-label="Close notification">
        {'\u00D7'}
      </button>
    </div>
  );
};

/* ================================================================== */
/*  Skeleton                                                           */
/* ================================================================== */

const SkeletonRows: React.FC = () => (
  <div className="wh-log-skeleton-list">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="wh-log-skeleton-row">
        <div className="wh-log-skeleton-circle" />
        <div className="wh-log-skeleton-lines">
          <div className="wh-log-skeleton-line long" />
          <div className="wh-log-skeleton-line medium" />
        </div>
      </div>
    ))}
  </div>
);

/* ================================================================== */
/*  Initial Form State                                                 */
/* ================================================================== */

const INITIAL_FORM = {
  date: todayISO(),
  mood: '',
  energy_level: '',
  sleep_quality: '',
  acne_level: '',
  symptoms: [] as number[],
  notes: '',
};

/* ================================================================== */
/*  Main Component                                                     */
/* ================================================================== */

const DailyLog: React.FC = () => {
  const { t } = useTranslation(['womensHealth', 'common']);
  const { activeProfile } = useProfile();
  const { canAccess } = useServiceAccess();
  const navigate = useNavigate();

  // Data state
  const [logs, setLogs] = useState<DailyLogType[]>([]);
  const [symptoms, setSymptoms] = useState<Symptom[]>([]);
  const [choices, setChoices] = useState<ChoicesData>({});

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);

  // Toast helper
  const showToast = useCallback((message: string, type: ToastState['type']) => {
    setToast({ message, type });
  }, []);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [logsData, symptomsData, choicesData] = await Promise.all([
        womensHealthService.getDailyLogs(),
        womensHealthService.getSymptomsByCategory('menstrual'),
        womensHealthService.getDailyLogChoices(),
      ]);
      setLogs(Array.isArray(logsData) ? logsData : []);
      setSymptoms(Array.isArray(symptomsData) ? symptomsData : []);
      if (choicesData) setChoices(choicesData);

      // Check if there is a log for today
      const todayLog = await womensHealthService.getDailyLogByDate(todayISO());
      if (todayLog) {
        setFormData({
          date: todayLog.date,
          mood: todayLog.mood || '',
          energy_level: todayLog.energy_level || '',
          sleep_quality: todayLog.sleep_quality || '',
          acne_level: todayLog.acne_level || '',
          symptoms: todayLog.symptoms || [],
          notes: todayLog.notes || '',
        });
        setEditingId(todayLog.id);
      }
    } catch (err) {
      console.error('Failed to fetch daily log data:', err);
      showToast('Failed to load daily log data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData, activeProfile]);

  // Sorted logs
  const sortedLogs = useMemo(() => {
    return [...logs].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [logs]);

  // Form handlers
  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM);
    setEditingId(null);
  }, []);

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSymptomToggle = (symptomId: number) => {
    setFormData((prev) => {
      const exists = prev.symptoms.includes(symptomId);
      return {
        ...prev,
        symptoms: exists
          ? prev.symptoms.filter((id) => id !== symptomId)
          : [...prev.symptoms, symptomId],
      };
    });
  };

  const handleLoadLog = (log: DailyLogType) => {
    setFormData({
      date: log.date,
      mood: log.mood || '',
      energy_level: log.energy_level || '',
      sleep_quality: log.sleep_quality || '',
      acne_level: log.acne_level || '',
      symptoms: log.symptoms || [],
      notes: log.notes || '',
    });
    setEditingId(log.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async () => {
    if (!formData.date) {
      showToast('Please select a date.', 'error');
      return;
    }

    const hasContent = Boolean(
      formData.mood ||
      formData.energy_level ||
      formData.sleep_quality ||
      formData.acne_level ||
      (formData.symptoms && formData.symptoms.length > 0) ||
      formData.notes.trim()
    );
    if (!hasContent) {
      showToast('Please fill in at least one field before saving the log.', 'error');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        date: formData.date,
        mood: formData.mood || undefined,
        energy_level: formData.energy_level || undefined,
        sleep_quality: formData.sleep_quality || undefined,
        acne_level: formData.acne_level || undefined,
        symptoms: formData.symptoms,
        notes: formData.notes.trim(),
      };

      if (editingId !== null) {
        await womensHealthService.updateDailyLog(editingId, payload);
        showToast('Daily log updated successfully!', 'success');
      } else {
        await womensHealthService.createDailyLog(payload);
        showToast('Daily log saved successfully!', 'success');
      }

      resetForm();
      await fetchData();
    } catch (err) {
      console.error('Error saving daily log:', err);
      showToast('Failed to save daily log. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await womensHealthService.deleteDailyLog(id);
      setLogs((prev) => prev.filter((l) => l.id !== id));
      if (editingId === id) resetForm();
      showToast('Daily log deleted.', 'success');
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error deleting daily log:', err);
      showToast('Failed to delete daily log.', 'error');
    }
  };

  /* ================================================================ */
  /*  Render: Select Field Helper                                      */
  /* ================================================================ */

  const renderSelectField = (
    label: string,
    icon: IconDefinition,
    field: string,
    options: ChoiceOption[],
    colorMap?: Record<string, string>,
  ) => (
    <div className="wh-log-form-group">
      <label className="wh-log-form-label">
        <FontAwesomeIcon icon={icon} style={{ marginRight: 6, opacity: 0.5 }} />
        {label}
      </label>
      <div className="wh-log-choice-grid">
        {options.map((opt) => {
          const isActive = formData[field as keyof typeof formData] === opt.value;
          const color = colorMap?.[opt.value] || '#f472b6';
          return (
            <button
              key={opt.value}
              type="button"
              className={`wh-log-choice-btn ${isActive ? 'active' : ''}`}
              style={isActive ? { background: color, borderColor: color, color: '#fff' } : { borderColor: `${color}40` }}
              onClick={() => handleFieldChange(field, opt.value)}
            >
              {field === 'mood' && MOOD_EMOJIS[opt.value] && (
                <span className="wh-log-choice-emoji">{MOOD_EMOJIS[opt.value]}</span>
              )}
              {opt.display_name}
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Render: Form                                                     */
  /* ================================================================ */

  const renderForm = () => (
    <div className="wh-log-form-card">
      <div className="wh-log-form-card-header">
        <h3>
          <FontAwesomeIcon icon={editingId ? faPen : faPlus} />
          {editingId ? 'Update Log' : 'Quick Log'}
        </h3>
        <div className="wh-log-today-badge">
          <FontAwesomeIcon icon={faCalendarDay} />
          {formatDateLong(formData.date)}
        </div>
      </div>

      <div className="wh-log-form-body">
        {/* Date */}
        <div className="wh-log-form-group">
          <label className="wh-log-form-label">
            <FontAwesomeIcon icon={faCalendarDay} style={{ marginRight: 6, opacity: 0.5 }} />
            Date
          </label>
          <input
            type="date"
            className="wh-log-form-input"
            value={formData.date}
            onChange={(e) => handleFieldChange('date', e.target.value)}
          />
        </div>

        {/* Mood */}
        {choices.mood && renderSelectField(t('womensHealth:dailyLog.mood'), faFaceSmile, 'mood', choices.mood, MOOD_COLORS)}

        {/* Energy Level */}
        {choices.energy_level && renderSelectField(t('womensHealth:dailyLog.energy'), faBolt, 'energy_level', choices.energy_level, ENERGY_COLORS)}

        {/* Sleep Quality */}
        {choices.sleep_quality && renderSelectField(t('womensHealth:dailyLog.sleep'), faMoon, 'sleep_quality', choices.sleep_quality, SLEEP_COLORS)}

        {/* Acne Level */}
        {choices.acne_level && renderSelectField('Acne Level', faDroplet, 'acne_level', choices.acne_level)}

        {/* Symptoms */}
        <div className="wh-log-form-group">
          <label className="wh-log-form-label">
            <FontAwesomeIcon icon={faNotesMedical} style={{ marginRight: 6, opacity: 0.5 }} />
            {t('womensHealth:dailyLog.symptoms')}
          </label>
          {symptoms.length > 0 ? (
            <div className="wh-log-symptoms-grid">
              {symptoms.map((symptom) => {
                const checked = formData.symptoms.includes(symptom.id);
                return (
                  <label
                    key={symptom.id}
                    className={`wh-log-symptom-option ${checked ? 'active' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleSymptomToggle(symptom.id)}
                    />
                    <span className="wh-log-symptom-checkbox">
                      {checked && <FontAwesomeIcon icon={faCheck} />}
                    </span>
                    <span className="wh-log-symptom-name">{symptom.name}</span>
                  </label>
                );
              })}
            </div>
          ) : (
            <p className="wh-log-form-hint">No symptoms available.</p>
          )}
        </div>

        {/* Notes */}
        <div className="wh-log-form-group">
          <label className="wh-log-form-label">
            <FontAwesomeIcon icon={faNotesMedical} style={{ marginRight: 6, opacity: 0.5 }} />
            {t('womensHealth:dailyLog.notes')}
          </label>
          <textarea
            className="wh-log-form-textarea"
            placeholder={t('womensHealth:dailyLog.notesPlaceholder')}
            rows={3}
            value={formData.notes}
            onChange={(e) => handleFieldChange('notes', e.target.value)}
          />
        </div>

        {/* Actions */}
        <div className="wh-log-form-actions">
          {editingId && (
            <button
              type="button"
              className="wh-log-btn wh-log-btn-cancel"
              onClick={resetForm}
            >
              {t('common:buttons.cancel')}
            </button>
          )}
          <button
            type="button"
            className="wh-log-btn wh-log-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
            ) : editingId !== null ? (
              <><FontAwesomeIcon icon={faCheck} /> {t('common:buttons.update')}</>
            ) : (
              <><FontAwesomeIcon icon={faPlus} /> {t('womensHealth:dailyLog.saveLog')}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Render: Log History                                              */
  /* ================================================================ */

  const renderLogHistory = () => {
    if (loading) return <SkeletonRows />;

    if (sortedLogs.length === 0) {
      return (
        <div className="wh-log-empty-state">
          <div className="wh-log-empty-icon-wrap">
            <FontAwesomeIcon icon={faFaceSmile} />
          </div>
          <h3>No daily logs yet</h3>
          <p>Start logging your daily health to track patterns and mood trends over time.</p>
        </div>
      );
    }

    return (
      <div className="wh-log-history-list">
        {sortedLogs.map((log, idx) => {
          const moodColor = MOOD_COLORS[log.mood] || '#94a3b8';
          const moodEmoji = MOOD_EMOJIS[log.mood] || '😐';
          const energyColor = ENERGY_COLORS[log.energy_level] || '#94a3b8';
          const sleepColor = SLEEP_COLORS[log.sleep_quality] || '#94a3b8';
          const isExpanded = expandedLogId === log.id;

          return (
            <div
              key={log.id}
              className={`wh-log-history-item ${isExpanded ? 'expanded' : ''}`}
              style={{ animationDelay: `${idx * 0.04}s` }}
            >
              <div
                className="wh-log-history-row"
                onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
              >
                <div className="wh-log-history-date">
                  <span className="wh-log-history-emoji">{moodEmoji}</span>
                  <div>
                    <span className="wh-log-history-date-text">{formatDate(log.date)}</span>
                    {log.date === todayISO() && (
                      <span className="wh-log-today-label">Today</span>
                    )}
                  </div>
                </div>
                <div className="wh-log-history-badges">
                  {log.mood_display && (
                    <span className="wh-log-mini-badge" style={{ background: `${moodColor}15`, color: moodColor, borderColor: `${moodColor}30` }}>
                      {log.mood_display}
                    </span>
                  )}
                  {log.energy_level_display && (
                    <span className="wh-log-mini-badge" style={{ background: `${energyColor}15`, color: energyColor, borderColor: `${energyColor}30` }}>
                      <FontAwesomeIcon icon={faBolt} /> {log.energy_level_display}
                    </span>
                  )}
                  {log.sleep_quality_display && (
                    <span className="wh-log-mini-badge" style={{ background: `${sleepColor}15`, color: sleepColor, borderColor: `${sleepColor}30` }}>
                      <FontAwesomeIcon icon={faMoon} /> {log.sleep_quality_display}
                    </span>
                  )}
                </div>
                <div className="wh-log-history-expand-icon">
                  <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                </div>
              </div>

              {/* Expanded details */}
              {isExpanded && (
                <div className="wh-log-history-details">
                  <div className="wh-log-history-detail-grid">
                    {log.acne_level_display && (
                      <div className="wh-log-history-detail-item">
                        <span className="wh-log-detail-label">Acne</span>
                        <span className="wh-log-detail-value">{log.acne_level_display}</span>
                      </div>
                    )}
                  </div>

                  {log.symptom_details && log.symptom_details.length > 0 && (
                    <div className="wh-log-history-symptoms">
                      {log.symptom_details.map((s) => (
                        <span key={s.id} className="wh-log-symptom-chip">{s.name}</span>
                      ))}
                    </div>
                  )}

                  {log.notes && (
                    <div className="wh-log-history-notes">
                      <FontAwesomeIcon icon={faNotesMedical} />
                      <span>{log.notes}</span>
                    </div>
                  )}

                  <div className="wh-log-history-item-actions">
                    <button
                      className="wh-log-action-btn wh-log-action-edit"
                      onClick={(e) => { e.stopPropagation(); handleLoadLog(log); }}
                    >
                      <FontAwesomeIcon icon={faPen} /> {t('common:buttons.edit')}
                    </button>
                    <button
                      className="wh-log-action-btn wh-log-action-delete"
                      onClick={(e) => { e.stopPropagation(); setConfirmDelete(log.id); }}
                    >
                      <FontAwesomeIcon icon={faTrash} /> {t('common:buttons.delete')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  /* ================================================================ */
  /*  Main Render                                                      */
  /* ================================================================ */

  return (
    <div className="wh-log-page">
      {/* Toast */}
      {toast && (
        <div className="wh-log-toast-container">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete Daily Log?"
        message="Are you sure you want to delete this log entry? This action cannot be undone."
        confirmLabel={t('common:buttons.delete')}
        cancelLabel={t('common:buttons.cancel')}
        variant="danger"
        onConfirm={() => { if (confirmDelete !== null) handleDelete(confirmDelete); }}
        onClose={() => setConfirmDelete(null)}
      />

      {/* Hero Banner */}
      <section className="wh-log-hero">
        <div className="wh-log-hero-circle wh-log-hero-circle-1" />
        <div className="wh-log-hero-circle wh-log-hero-circle-2" />
        <Container>
          <BackButton />
          <div className="wh-log-hero-content">
            <div className="wh-log-hero-badge">
              <FontAwesomeIcon icon={faFaceSmile} />
              Puberty &amp; Menstrual
            </div>
            <h1 className="wh-log-hero-title">{t('womensHealth:dailyLog.title')}</h1>
            <p className="wh-log-hero-subtitle">
              Track your mood, energy, sleep quality, and symptoms daily to discover patterns and take charge of your wellbeing.
            </p>
          </div>
        </Container>
      </section>

      {/* Main Content */}
      <Container>
        {/* Today's prominent date display */}
        <div className="wh-log-today-strip">
          <div className="wh-log-today-strip-inner">
            <FontAwesomeIcon icon={faCalendarDay} />
            <span>{formatDateLong(todayISO())}</span>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="wh-log-main-grid">
          {/* Left: Quick-log form */}
          <div className="wh-log-main-left">
            {renderForm()}
          </div>

          {/* Right: Log history */}
          <div className="wh-log-main-right">
            <div className="wh-log-history-card">
              <div className="wh-log-history-card-header">
                <h3>
                  <FontAwesomeIcon icon={faCalendarDay} />
                  Log History
                </h3>
                <span className="wh-log-history-count">{logs.length} entries</span>
              </div>
              {renderLogHistory()}
            </div>
          </div>
        </div>

        {/* Medicine Reminders Callout */}
        {canAccess('medicine-reminder') && (
        <div className="wh-log-callout-card">
          <div className="wh-log-callout-icon">
            <FontAwesomeIcon icon={faCapsules} />
          </div>
          <div className="wh-log-callout-text">
            <h4>Medicine Reminders</h4>
            <p>Never miss a dose. Set up reminders for pain relief, supplements, and prescribed medications.</p>
          </div>
          <button
            className="wh-log-btn wh-log-btn-primary"
            onClick={() => navigate('/medicine-reminder')}
          >
            Go to Reminders <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </div>
        )}
      </Container>
    </div>
  );
};

export default DailyLog;
